import { Globe } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/shared/components/ui';

type CustomerDetailsShape = {
  name: string;
  companyType?: string;
  industry?: string;
  website?: string;
};

interface CustomerDetailsFieldsProps<TForm extends FieldValues & CustomerDetailsShape> {
  control: Control<TForm>;
  disabled?: boolean;
  labels: {
    name: ReactNode;
    companyType: ReactNode;
    industry: ReactNode;
    website: ReactNode;
  };
  placeholders?: Partial<Record<keyof CustomerDetailsShape, string>>;
  websiteWithIcon?: boolean;
}

export function CustomerDetailsFields<TForm extends FieldValues & CustomerDetailsShape>({
  control,
  disabled,
  labels,
  placeholders,
  websiteWithIcon = false,
}: CustomerDetailsFieldsProps<TForm>) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <FormField
        control={control}
        name={'name' as Path<TForm>}
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{labels.name}</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={placeholders?.name} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={'companyType' as Path<TForm>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{labels.companyType}</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={placeholders?.companyType} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={'industry' as Path<TForm>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{labels.industry}</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={placeholders?.industry} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={'website' as Path<TForm>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{labels.website}</FormLabel>
            <FormControl>
              {websiteWithIcon ? (
                <div className="relative">
                  <Globe
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    {...field}
                    disabled={disabled}
                    type="url"
                    className="pl-9"
                    placeholder={placeholders?.website}
                  />
                </div>
              ) : (
                <Input
                  {...field}
                  disabled={disabled}
                  type="url"
                  placeholder={placeholders?.website}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
